import { isEqual } from 'lodash';
import {
  resourceQueryUtils,
  type ResourceSearchQuery
} from 'v5/api/resource/resource.api';
import type { ReportEditorState } from 'v5/core/reporting/reportTemplateQuery';
import type {
  FilterWidgetValue,
  PerFieldConfigBase
} from 'v5/patterns/filters/FilterWidget.types';
import {
  reportDateFilterOf,
  reportDateValueOf,
  type ReportDateFilter
} from './reportDateFilter';

export type ReportFilterBarValue = FilterWidgetValue<PerFieldConfigBase>;

/** A date chip's key names its field: the bar holds one chip per field, the state one date. */
const REPORT_DATE_KEY_PREFIX = 'date:';
export const REPORT_BOOKED_KEY = 'booked';

export const reportDateKeyOf = (field: string) =>
  `${REPORT_DATE_KEY_PREFIX}${field}`;

const dateFieldOfKey = (key: string) =>
  key.startsWith(REPORT_DATE_KEY_PREFIX)
    ? key.slice(REPORT_DATE_KEY_PREFIX.length)
    : null;

const DEFAULT_ARCHIVED = resourceQueryUtils.ofDefault().filters.archived;

export interface ReportFilterBar {
  value: ReportFilterBarValue;
  /** The stored date or "booked" condition the chips can't show (a range missing an end). */
  unreadable: ('date' | 'booked')[];
}

function bookedFilterOf(
  booked: NonNullable<ReportEditorState['booked']>
): ReportDateFilter | null {
  const filter = reportDateFilterOf({
    field: REPORT_BOOKED_KEY,
    kind: 'range',
    start: booked.startDate,
    end: booked.endDate
  });
  return filter?.operator === 'equal'
    ? { operator: 'between', value: { from: filter.value, to: filter.value } }
    : filter;
}

/** The editor state as the filter bar's chips. */
export function reportFilterBarOf(state: ReportEditorState): ReportFilterBar {
  const resourceEntries = Object.entries(state.resourceSearch.filters).filter(
    ([key, filter]) =>
      filter && !(key === 'archived' && isEqual(filter, DEFAULT_ARCHIVED))
  );
  const date = state.date && reportDateFilterOf(state.date);
  const booked = state.booked && bookedFilterOf(state.booked);
  return {
    value: {
      ...(state.date && date && { [reportDateKeyOf(state.date.field)]: date }),
      ...Object.fromEntries(resourceEntries),
      ...(booked && { [REPORT_BOOKED_KEY]: booked })
    },
    unreadable: [
      ...(state.date && !date ? ['date' as const] : []),
      ...(state.booked && !booked ? ['booked' as const] : [])
    ]
  };
}

function dateOf(
  state: ReportEditorState,
  before: ReportFilterBarValue,
  next: ReportFilterBarValue
): ReportEditorState['date'] {
  const changed = Object.keys({ ...before, ...next }).filter(
    key =>
      dateFieldOfKey(key) !== null &&
      !isEqual(before[key] ?? null, next[key] ?? null)
  );
  if (changed.length === 0) return state.date;
  // a chip added beside the current one replaces it: a template stores one date
  const added = changed.find(key => !before[key] && next[key]);
  const key = added ?? changed[0];
  const filter = next[key];
  const field = dateFieldOfKey(key);
  return filter && field !== null
    ? reportDateValueOf(field, filter as ReportDateFilter)
    : null;
}

function bookedOf(
  state: ReportEditorState,
  before: ReportFilterBarValue,
  next: ReportFilterBarValue
): ReportEditorState['booked'] {
  const filter = next[REPORT_BOOKED_KEY] as ReportDateFilter | null | undefined;
  if (isEqual(before[REPORT_BOOKED_KEY] ?? null, filter ?? null)) {
    return state.booked;
  }
  if (!filter || !state.booked) return null;
  const range = reportDateValueOf(REPORT_BOOKED_KEY, filter);
  return range.kind === 'range' && range.start !== null && range.end !== null
    ? { ...state.booked, startDate: range.start, endDate: range.end }
    : state.booked;
}

/**
 * The editor state after the bar went from `before` to `next`: only the chips that changed are
 * read back, so an untouched condition keeps its stored value (a date's time part, a "booked"
 * chip's statuses).
 */
export function reportEditorStateWith(
  state: ReportEditorState,
  before: ReportFilterBarValue,
  next: ReportFilterBarValue
): ReportEditorState {
  const changedFilters = Object.fromEntries(
    Object.keys({ ...before, ...next })
      .filter(
        key =>
          dateFieldOfKey(key) === null &&
          key !== REPORT_BOOKED_KEY &&
          !isEqual(before[key] ?? null, next[key] ?? null)
      )
      .map(key => [key, next[key] ?? null])
  );
  return {
    ...state,
    date: dateOf(state, before, next),
    booked: bookedOf(state, before, next),
    resourceSearch: {
      ...state.resourceSearch,
      // the bar's chips are the search's own filters, read back by key
      filters: {
        ...state.resourceSearch.filters,
        ...changedFilters
      } as ResourceSearchQuery['filters']
    }
  };
}
